import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { getClient } from '@/features/clients/api/clients';
import {
  getLocationReports,
  getServiceLocation,
} from '@/features/service-locations/api/serviceLocations';
import { ServiceReportDetails } from '@/features/service-locations/components/ServiceReportDialog';

export function ServiceReportPage() {
  const navigate = useNavigate();
  const params = useParams();
  const clientId = Number(params.clientId);
  const locationId = Number(params.locationId);
  const reportId = Number(params.reportId);
  const query = useQuery({
    queryKey: ['service-report', clientId, locationId, reportId],
    queryFn: async () => {
      const [client, location, reports] = await Promise.all([
        getClient(clientId),
        getServiceLocation(clientId, locationId),
        getLocationReports(clientId, locationId),
      ]);
      return { client, location, report: reports.find((report) => report.id === reportId) };
    },
    enabled: clientId > 0 && locationId > 0 && reportId > 0,
  });
  if (query.isLoading)
    return (
      <Stack alignItems="center" sx={{ p: 8 }}>
        <CircularProgress />
      </Stack>
    );
  if (query.isError || !query.data?.report)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        This service report could not be loaded.
      </Alert>
    );
  const { client, location, report } = query.data;
  return (
    <Box sx={{ p: 1 }}>
      <Paper elevation={0} sx={paperSx}>
        <Stack direction="row" alignItems="center" sx={{ minHeight: 44, px: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            sx={commandButtonSx}
            onClick={() => void navigate(-1)}
          >
            Back
          </Button>
        </Stack>
      </Paper>
      <Paper elevation={0} sx={{ ...paperSx, mt: 1, p: 2.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="h1">Service report</Typography>
          <Chip label="Technician submission - Read only" variant="outlined" />
        </Stack>
        <Typography color="text.secondary">
          {client.name} · {location.name}
        </Typography>
      </Paper>
      <Paper elevation={0} sx={{ ...paperSx, mt: 1.5, p: 2 }}>
        <ServiceReportDetails report={report} />
      </Paper>
    </Box>
  );
}
const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
};
const commandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'primary.main' },
};
